import { useFluent } from 'fluent-vue'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import { type Props as StatusMessageProps } from '@/app/common/TStatusMessage.vue'
import { announce } from '@/utilities/assistiveTechnology.ts'
import { uniqueId } from '@/utilities/uniqueId.ts'

export interface Notification {
	type: StatusMessageProps['type']
	text: string
	link?: StatusMessageProps['link']
	/** Default: `true` */
	timer?: boolean
}

const TIMEOUT_IN_MS = 6000

export const useNotificationsStore = defineStore('notifications', () => {
	const { $t } = useFluent()

	const list = ref(new Map<string, Notification>())
	const timers = new Map<string, number>()

	function add(notification: Notification) {
		const id = uniqueId()
		const announceText = `${$t('global-status-message')}: ${notification.text}`

		list.value.set(id, notification)
		startTimer(id)
		announce('polite', announceText)
	}

	function remove(id: string) {
		list.value.delete(id)
		stopTimer(id)
	}

	function startTimer(id: string) {
		if (list.value.get(id)?.timer === false) return

		stopTimer(id)

		const timerId = window.setTimeout(() => remove(id), TIMEOUT_IN_MS)
		timers.set(id, timerId)
	}

	function stopTimer(id: string) {
		clearTimeout(timers.get(id))
		timers.delete(id)
	}

	return { list, add, remove, startTimer, stopTimer }
})
